import React, { useState, useRef } from 'react';
import { Plus, Trash2, Package, X, Upload, Download, ChevronDown, ChevronUp, Edit2, ArrowLeftRight, ArrowUpDown, Circle, AlertTriangle } from 'lucide-react';
import { useCutList } from '../../contexts/CutListContext';
import { useAuth } from '../../contexts/AuthContext';
import { formatDimension, formatPreciseDimension, getCSVUnitLabel, roundForCSV, UNIT_SYSTEMS, getUnitDefaults, getThicknessTolerance, parseMathExpr } from '../../utils/units';
import { uid } from '../../utils/uid';
import CSVColumnMapper from '../../components/CSVColumnMapper';
import ConfirmDialog from '../../shared/ConfirmDialog';

/**
 * Step 2: Parts - Add and manage pieces
 * Stock type grouping with per-type sheet configuration
 * Includes CSV import support
 */
const PartsStep = ({ onNext }) => {
  const {
    pieces,
    addPiece,
    addPieces,
    updatePiece,
    removePiece,
    currentUnits,
    handleUnitChange,
    sheetWidth,
    sheetLength,
    addError,
    layouts,
    // Stock sheet types
    stockSheetTypes,
    addStockSheetType,
    updateStockSheetType,
    removeStockSheetType,
  } = useCutList();

  const {
    checkImportAllowed,
  } = useAuth();

  const [showBottomSheet, setShowBottomSheet] = useState(false);
  const [editingPiece, setEditingPiece] = useState(null);
  const fileInputRef = useRef(null);

  // CSV state
  const [showColumnMapper, setShowColumnMapper] = useState(false);
  const [csvData, setCsvData] = useState({ headers: [], rows: [] });

  // Stock type modal state
  const [showStockTypeSheet, setShowStockTypeSheet] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, name }
  const [deletePieceConfirm, setDeletePieceConfirm] = useState(null); // { id, name }
  const [editingStockType, setEditingStockType] = useState(null);
  const [stockTypeForm, setStockTypeForm] = useState({
    name: '',
    thickness: '',
    width: '',
    length: ''
  });

  // Selected stock type for adding pieces
  const [selectedStockTypeId, setSelectedStockTypeId] = useState(null);

  // Collapsed state for stock type groups
  const [collapsedGroups, setCollapsedGroups] = useState({});

  // Last used piece parameters persistence (width and length only, thickness comes from sheet)
  const LAST_PIECE_PARAMS_KEY = 'cutcalc-last-piece-params';

  const getLastPieceParams = () => {
    try {
      const saved = localStorage.getItem(LAST_PIECE_PARAMS_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to load last piece params:', e);
    }
    // Default values (no thickness - that comes from the sheet)
    return {
      width: currentUnits.system === UNIT_SYSTEMS.IMPERIAL ? 8 : 20,
      length: currentUnits.system === UNIT_SYSTEMS.IMPERIAL ? 12 : 30,
    };
  };

  // Normalize lastParams: migrate stale localStorage with old 'height' key
  const normalizeLastParams = (params) => {
    const w = params.width ?? (currentUnits.system === UNIT_SYSTEMS.IMPERIAL ? 8 : 20);
    const l = params.length ?? params.height ?? (currentUnits.system === UNIT_SYSTEMS.IMPERIAL ? 12 : 30);
    return { width: w, length: l };
  };

  const saveLastPieceParams = (width, length) => {
    try {
      localStorage.setItem(LAST_PIECE_PARAMS_KEY, JSON.stringify({
        width, length,
      }));
    } catch (e) {
      console.error('Failed to save last piece params:', e);
    }
  };

  // Get thickness from selected sheet or default
  const getThicknessForSelectedSheet = (stockTypeId) => {
    if (stockTypeId) {
      const sheet = stockSheetTypes.find(t => t.id === stockTypeId);
      if (sheet) return sheet.thickness;
    }
    return getUnitDefaults(currentUnits.unit, currentUnits.system).thickness;
  };

  // Get sheet dimensions for a stock type (or default)
  const getSheetDimensionsForStockType = (stockTypeId) => {
    if (stockTypeId) {
      const sheet = stockSheetTypes.find(t => t.id === stockTypeId);
      if (sheet) return { width: sheet.width, length: sheet.length };
    }
    return { width: sheetWidth, length: sheetLength };
  };

  // Check if a grain direction is valid for given piece and sheet dimensions
  // Returns true if the piece can fit on the sheet with that grain direction
  const isGrainDirectionValid = (pieceW, pieceL, grain, sheetW, sheetL) => {
    // 'horizontal' = Along Length: piece fits normally (pieceL along sheetL, pieceW along sheetW)
    const fitsNormal = pieceL <= sheetL && pieceW <= sheetW;
    // 'vertical' = Along Width: piece is rotated (pieceW along sheetL, pieceL along sheetW)
    const fitsRotated = pieceW <= sheetL && pieceL <= sheetW;

    if (grain === 'none') {
      // Only valid if BOTH orientations work (otherwise grain should be specified)
      return fitsNormal && fitsRotated;
    }
    if (grain === 'horizontal') return fitsNormal;
    if (grain === 'vertical') return fitsRotated;
    return true;
  };

  // State for grain direction warning toast
  const [grainWarning, setGrainWarning] = useState(null);

  // Auto-select the best valid grain direction for given dimensions
  const getAutoGrainDirection = (pieceW, pieceL, sheetW, sheetL) => {
    const noneValid = isGrainDirectionValid(pieceW, pieceL, 'none', sheetW, sheetL);
    if (noneValid) return 'none';
    const horizValid = isGrainDirectionValid(pieceW, pieceL, 'horizontal', sheetW, sheetL);
    if (horizValid) return 'horizontal';
    const vertValid = isGrainDirectionValid(pieceW, pieceL, 'vertical', sheetW, sheetL);
    if (vertValid) return 'vertical';
    return 'none'; // fallback, will show warning
  };

  // Group pieces by stock type
  const groupPiecesByStockType = () => {
    const groups = {};
    const tolerance = getThicknessTolerance(currentUnits.unit, currentUnits.system);

    // Initialize groups for all stock types
    stockSheetTypes.forEach(type => {
      groups[type.id] = { stockType: type, pieces: [] };
    });

    // Add unassigned group with default thickness
    const defaultThickness = getUnitDefaults(currentUnits.unit, currentUnits.system).thickness;
    groups['unassigned'] = {
      stockType: { id: 'unassigned', name: 'Unassigned (Default)', width: sheetWidth, length: sheetLength, thickness: defaultThickness },
      pieces: []
    };

    pieces.forEach(piece => {
      let stockType = null;

      // Check explicit assignment
      if (piece.stockSheetTypeId) {
        stockType = stockSheetTypes.find(t => t.id === piece.stockSheetTypeId);
      }

      // Auto-match by thickness if no explicit assignment
      if (!stockType) {
        stockType = stockSheetTypes.find(t =>
          Math.abs(t.thickness - (piece.thickness || 0)) < tolerance
        );
      }

      const key = stockType ? stockType.id : 'unassigned';
      if (groups[key]) {
        groups[key].pieces.push(piece);
      } else {
        groups['unassigned'].pieces.push(piece);
      }
    });

    // Return as array, filter out empty stock type groups (but keep unassigned if it has pieces)
    return Object.values(groups).filter(g =>
      g.pieces.length > 0 || (g.stockType.id !== 'unassigned')
    );
  };

  const stockTypeGroups = groupPiecesByStockType();

  // Count physical sheets needed per stock type from optimization results
  const getSheetsNeededForStockType = (stockTypeId) => {
    if (!layouts || layouts.length === 0) return 0;
    if (stockTypeId === 'unassigned') {
      return layouts.filter(l => !l.stockTypeId || l.stockTypeId === 'unassigned' || l.stockTypeId === 'default').length;
    }
    return layouts.filter(l => l.stockTypeId === stockTypeId).length;
  };

  // Format thickness for display (snap for sheets)
  const formatThickness = (thicknessVal) => {
    if (!thicknessVal || thicknessVal === 0) {
      return 'No thickness';
    }
    return formatDimension(thicknessVal, currentUnits.unit, currentUnits.system, { snap: true });
  };

  // Format sheet dimensions for display (with snap for clean values)
  const formatSheetSize = (width, length) => {
    const wStr = formatDimension(width, currentUnits.unit, currentUnits.system, { includeUnit: false, snap: true });
    const lStr = formatDimension(length, currentUnits.unit, currentUnits.system, { includeUnit: false, snap: true });
    return `${wStr}×${lStr}`;
  };

  // Open stock type sheet to add new type
  const openAddStockType = () => {
    const defs = getUnitDefaults(currentUnits.unit, currentUnits.system);
    setStockTypeForm({
      name: '',
      thickness: defs.thickness.toString(),
      width: defs.sheetW.toString(),
      length: defs.sheetL.toString()
    });
    setEditingStockType(null);
    setShowStockTypeSheet(true);
  };

  // Open stock type sheet to edit existing type
  const openEditStockType = (stockType) => {
    setStockTypeForm({
      name: stockType.name,
      thickness: stockType.thickness.toString(),
      width: stockType.width.toString(),
      length: stockType.length.toString()
    });
    setEditingStockType(stockType);
    setShowStockTypeSheet(true);
  };

  // Save stock type (add or update)
  const saveStockType = () => {
    const thickness = parseMathExpr(stockTypeForm.thickness) || 0;
    const width = parseMathExpr(stockTypeForm.width);
    const length = parseMathExpr(stockTypeForm.length);

    if (width <= 0 || length <= 0) {
      addError('Width and length must be greater than 0');
      return;
    }

    const name = stockTypeForm.name || `Stock ${stockSheetTypes.length + 1}`;

    if (editingStockType) {
      updateStockSheetType(editingStockType.id, {
        name,
        thickness,
        width,
        length
      });
    } else {
      const newId = addStockSheetType({
        name,
        thickness,
        width,
        length
      });
      // Select the new stock type for adding pieces
      setSelectedStockTypeId(newId);
    }
    setShowStockTypeSheet(false);
  };

  // Toggle group collapse
  const toggleGroupCollapse = (stockTypeId) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [stockTypeId]: !prev[stockTypeId]
    }));
  };

  // Form state for bottom sheet (no thickness - that's inherited from the sheet)
  const [formData, setFormData] = useState({
    name: '',
    width: '',
    length: '',
    quantity: '1',
    grainDirection: 'none',
    stockSheetTypeId: null,
  });

  const getUnitLabel = () => {
    if (currentUnits.system === UNIT_SYSTEMS.IMPERIAL) {
      return currentUnits.unit === 'inches' ? 'in' : 'ft';
    }
    return currentUnits.unit;
  };

  const openAddSheet = () => {
    const lastParams = normalizeLastParams(getLastPieceParams());

    // Auto-select grain direction based on remembered dimensions
    const pieceW = lastParams.width;
    const pieceL = lastParams.length;
    const { width: sheetW, length: sheetL } = getSheetDimensionsForStockType(selectedStockTypeId);
    const autoGrain = getAutoGrainDirection(pieceW, pieceL, sheetW, sheetL);

    setFormData({
      name: `Part ${pieces.length + 1}`,
      width: lastParams.width.toString(),
      length: lastParams.length.toString(),
      quantity: '1',
      grainDirection: autoGrain,
      stockSheetTypeId: selectedStockTypeId || null,
    });
    setEditingPiece(null);
    setShowBottomSheet(true);
  };

  const openEditSheet = (piece) => {
    setFormData({
      name: piece.name,
      width: piece.width.toString(),
      length: piece.length.toString(),
      quantity: piece.quantity.toString(),
      grainDirection: piece.grainDirection,
      stockSheetTypeId: piece.stockSheetTypeId || '',
    });
    setEditingPiece(piece);
    setShowBottomSheet(true);
  };

  // Evaluate math expressions on blur — replaces "9/16" with "0.5625" in the field
  const evalBlur = (value, setter) => {
    const parsed = parseMathExpr(value);
    if (!isNaN(parsed) && value !== String(parsed)) {
      setter(String(parsed));
    }
  };

  const handleSave = () => {
    const widthVal = parseMathExpr(formData.width) || 0;
    const lengthVal = parseMathExpr(formData.length) || 0;
    const widthInches = widthVal;
    const lengthInches = lengthVal;

    if (widthInches <= 0 || lengthInches <= 0) {
      alert('Width and length must be greater than 0');
      return;
    }

    if (editingPiece) {
      updatePiece(editingPiece.id, 'name', formData.name);
      updatePiece(editingPiece.id, 'width', widthInches);
      updatePiece(editingPiece.id, 'length', lengthInches);
      updatePiece(editingPiece.id, 'quantity', parseInt(formData.quantity) || 1);
      updatePiece(editingPiece.id, 'grainDirection', formData.grainDirection);
      // Update stock sheet type assignment (for moving pieces between sheets)
      const newStockTypeId = formData.stockSheetTypeId === '' ? null : formData.stockSheetTypeId;
      updatePiece(editingPiece.id, 'stockSheetTypeId', newStockTypeId);
      // Update thickness to match the target sheet
      const newThickness = getThicknessForSelectedSheet(newStockTypeId);
      updatePiece(editingPiece.id, 'thickness', newThickness);
    } else {
      // Save last used parameters for next time (width and length only)
      saveLastPieceParams(widthVal, lengthVal);

      // Get thickness from the target sheet
      const targetStockTypeId = formData.stockSheetTypeId || selectedStockTypeId;
      const thicknessInches = getThicknessForSelectedSheet(targetStockTypeId);

      addPiece({
        id: uid(),
        name: formData.name,
        width: widthInches,
        length: lengthInches,
        thickness: thicknessInches,
        quantity: parseInt(formData.quantity) || 1,
        material: 'Plywood',
        grainDirection: formData.grainDirection,
        stockSheetTypeId: targetStockTypeId,
      });
    }

    setShowBottomSheet(false);
  };

  // CSV Import handlers
  const handleClickCSVUpload = () => {
    fileInputRef.current?.click();
  };

  const handleCSVUpload = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.csv')) {
        addError('Please upload a CSV file');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target.result;
          const lines = text.split('\n').filter(line => line.trim());

          if (lines.length < 2) {
            addError('CSV file must have at least a header row and one data row');
            return;
          }

          // Check if first row is just a title (single cell or significantly fewer columns than row 2)
          const firstRowCells = lines[0].split(',').map(h => h.trim());
          const secondRowCells = lines[1].split(',').map(h => h.trim());

          // First row is a title if it has only 1 non-empty cell, and second row has more columns
          const firstRowNonEmpty = firstRowCells.filter(c => c.length > 0).length;
          const isFirstRowTitle = firstRowNonEmpty <= 1 && secondRowCells.length > 1;

          let headers, dataStartIndex;
          if (isFirstRowTitle) {
            headers = secondRowCells;
            dataStartIndex = 2;
          } else {
            headers = firstRowCells;
            dataStartIndex = 1;
          }

          if (lines.length <= dataStartIndex) {
            addError('CSV file must have at least a header row and one data row');
            return;
          }

          const rows = [];
          for (let i = dataStartIndex; i < lines.length; i++) {
            const values = lines[i].split(',').map(v => v.trim());
            rows.push(values);
          }

          setCsvData({ headers, rows });
          setShowColumnMapper(true);
        } catch (error) {
          addError(`Error processing CSV: ${error.message}`);
        }
      };
      reader.onerror = () => addError('Error reading CSV file');
      reader.readAsText(file);
    }
    // Reset input so the same file can be selected again
    if (event.target) {
      event.target.value = '';
    }
  };

  const processCSVWithMapping = async (columnMapping) => {
    // Check server-side rate limit for CSV import
    const rateCheck = await checkImportAllowed('csv');
    if (!rateCheck.allowed) {
      setShowColumnMapper(false);
      return; // promptUpgrade is called inside checkImportAllowed
    }

    const { rows } = csvData;
    const newPieces = [];

    const widthIdx = columnMapping.width !== '' ? parseInt(columnMapping.width) : -1;
    const heightIdx = columnMapping.length !== '' ? parseInt(columnMapping.length) : -1;
    const depthIdx = columnMapping.depth !== '' ? parseInt(columnMapping.depth) : -1;
    const quantityIdx = columnMapping.quantity !== '' ? parseInt(columnMapping.quantity) : -1;
    const nameIdx = columnMapping.name !== '' ? parseInt(columnMapping.name) : -1;
    const materialIdx = columnMapping.material !== '' ? parseInt(columnMapping.material) : -1;
    const grainIdx = columnMapping.grainDirection !== '' ? parseInt(columnMapping.grainDirection) : -1;
    const stockTypeIdx = columnMapping.stockType !== '' && columnMapping.stockType !== undefined ? parseInt(columnMapping.stockType) : -1;
    const sheetDimsIdx = columnMapping.sheetDimensions !== '' && columnMapping.sheetDimensions !== undefined ? parseInt(columnMapping.sheetDimensions) : -1;
    const detectedUnit = columnMapping.detectedUnit;

    // No conversion needed - values stored raw in current units
    const sheetDimsFactor = 1;

    // Default thickness from unit defaults
    const defs = getUnitDefaults(currentUnits.unit, currentUnits.system);
    const defaultThickness = defs.thickness;

    if (widthIdx === -1 || heightIdx === -1) {
      addError('Width and Length are required fields');
      setShowColumnMapper(false);
      return;
    }

    for (let i = 0; i < rows.length; i++) {
      const values = rows[i];
      if (values.length < Math.max(widthIdx, heightIdx) + 1) continue;

      let width = parseFloat(values[widthIdx] || '0');
      let height = parseFloat(values[heightIdx] || '0');
      let thickness = depthIdx !== -1 ? parseFloat(values[depthIdx] || '0') : 0;

      // Skip rows where width/height are not valid numbers
      if (isNaN(width) || isNaN(height)) continue;

      // No conversion - store values as-is in current units
      thickness = thickness > 0 ? thickness : defaultThickness;

      if (width <= 0 || height <= 0) continue;

      // Parse sheet dimensions from CSV (e.g., "2440x1220x18")
      const rawSheetDims = sheetDimsIdx !== -1 ? (values[sheetDimsIdx] || '').replace(/"/g, '').trim() : '';

      newPieces.push({
        id: uid(),
        name: nameIdx !== -1 ? values[nameIdx]?.replace(/"/g, '') : `Part ${i + 1}`,
        width: parseFloat(width.toFixed(4)),
        length: parseFloat(height.toFixed(4)),
        thickness: parseFloat(thickness.toFixed(4)),
        quantity: quantityIdx !== -1 ? parseInt(values[quantityIdx]) || 1 : 1,
        material: materialIdx !== -1 ? values[materialIdx] : 'Plywood',
        grainDirection: grainIdx !== -1 ? (() => {
          const g = (values[grainIdx] || '').toLowerCase().trim();
          // "Along Length" / "horizontal" = grain runs along the length (internal 'horizontal')
          if (g === 'along length' || g === 'horizontal' || g === 'length' || g === 'lengthwise' || g === 'long') return 'horizontal';
          // "Along Width" / "vertical" = grain runs along the width (internal 'vertical')
          if (g === 'along width' || g === 'vertical' || g === 'width' || g === 'widthwise' || g === 'short') return 'vertical';
          if (g === 'no preference' || g === 'none' || g === 'any' || g === 'n/a' || g === '') return 'none';
          return 'none';
        })() : 'none',
        stockSheetTypeId: null,
        _csvSheetName: stockTypeIdx !== -1 ? (values[stockTypeIdx] || '').replace(/"/g, '').trim() : '',
        _csvSheetDims: rawSheetDims
      });
    }

    if (newPieces.length > 0) {
      let piecesToAdd = newPieces;

      // Auto-create stock sheet types from imported pieces grouped by thickness + sheet type name
      const THICKNESS_TOL = getThicknessTolerance(currentUnits.unit, currentUnits.system);
      const groupMap = new Map();

      piecesToAdd.forEach((piece, idx) => {
        const csvSheetName = piece._csvSheetName || '';
        const thickness = piece.thickness || defaultThickness;

        // Find existing group with matching thickness and name
        let matched = false;
        for (const [, group] of groupMap) {
          if (Math.abs(group.thickness - thickness) < THICKNESS_TOL && group.name.toLowerCase() === csvSheetName.toLowerCase()) {
            group.pieceIndices.push(idx);
            if (!group.sheetDims && piece._csvSheetDims) group.sheetDims = piece._csvSheetDims;
            matched = true;
            break;
          }
        }
        if (!matched) {
          const key = `${thickness.toFixed(4)}|${csvSheetName}`;
          groupMap.set(key, { thickness, name: csvSheetName, pieceIndices: [idx], sheetDims: piece._csvSheetDims || '' });
        }
      });

      // Create stock types and assign pieces
      let sheetCounter = stockSheetTypes.length + 1;
      for (const [, group] of groupMap) {
        // Check if a matching stock type already exists
        let existingType = stockSheetTypes.find(t =>
          Math.abs(t.thickness - group.thickness) < THICKNESS_TOL &&
          (group.name ? t.name.toLowerCase() === group.name.toLowerCase() : true)
        );

        if (!existingType) {
          const name = group.name && group.name.toLowerCase() !== 'unassigned'
            ? group.name
            : `Stock ${sheetCounter++}`;
          // Parse sheet dimensions from CSV (e.g., "48x96x0.75" = Width x Length x Thickness)
          let sheetW = defs.sheetW, sheetL = defs.sheetL;
          if (group.sheetDims) {
            const parts = group.sheetDims.split(/x/i).map(s => parseFloat(s.trim()));
            if (parts.length >= 2 && parts[0] > 0 && parts[1] > 0) {
              sheetW = parseFloat((parts[0] * sheetDimsFactor).toFixed(4));
              sheetL = parseFloat((parts[1] * sheetDimsFactor).toFixed(4));
            }
          }
          const newId = addStockSheetType({
            name,
            thickness: group.thickness,
            width: sheetW,
            length: sheetL
          });
          existingType = { id: newId };
        }

        group.pieceIndices.forEach(idx => {
          piecesToAdd[idx] = { ...piecesToAdd[idx], stockSheetTypeId: existingType.id };
        });
      }

      // Clean up temp fields
      piecesToAdd.forEach(p => { delete p._csvSheetName; delete p._csvSheetDims; });

      addPieces(piecesToAdd);

      // Switch display units to match detected CSV unit
      if (detectedUnit) {
        const unitMapping = {
          'mm': { system: 'metric', unit: 'mm' },
          'cm': { system: 'metric', unit: 'cm' },
          'm': { system: 'metric', unit: 'm' },
          'in': { system: 'imperial', unit: 'inches' },
          'ft': { system: 'imperial', unit: 'feet' }
        };
        const unitConfig = unitMapping[detectedUnit];
        if (unitConfig) {
          handleUnitChange(unitConfig);
        }
      }

      if (piecesToAdd.length === newPieces.length) {
        addError(`Successfully imported ${newPieces.length} pieces`);
      }
    } else {
      addError('No valid pieces found in CSV');
    }

    setShowColumnMapper(false);
  };

  const exportCSV = () => {
    const unitLabel = getCSVUnitLabel(currentUnits.unit, currentUnits.system);
    const rows = [
      ['Part Name', `Width (${unitLabel})`, `Length (${unitLabel})`, `Thickness (${unitLabel})`, 'Quantity', 'Grain', 'Sheet Type', `Sheet Dimensions (${unitLabel})`]
    ];

    const tolerance = getThicknessTolerance(currentUnits.unit, currentUnits.system);
    pieces.forEach(piece => {
      // Match sheet by explicit ID first, then auto-match by thickness (same as grouping logic)
      let sheet = piece.stockSheetTypeId ? stockSheetTypes.find(t => t.id === piece.stockSheetTypeId) : null;
      if (!sheet) {
        sheet = stockSheetTypes.find(t => Math.abs(t.thickness - (piece.thickness || 0)) < tolerance);
      }
      const conv = (val) => roundForCSV(val, currentUnits.unit, currentUnits.system);
      const sheetDims = sheet ? `${conv(sheet.width)}x${conv(sheet.length)}x${conv(sheet.thickness)}` : '';
      rows.push([
        `"${(piece.name || '').replace(/"/g, '""')}"`,
        conv(piece.width),
        conv(piece.length),
        conv(piece.thickness),
        piece.quantity || 1,
        piece.grainDirection === 'horizontal' ? 'Along Length' : piece.grainDirection === 'vertical' ? 'Along Width' : 'No Preference',
        sheet ? `"${sheet.name.replace(/"/g, '""')}"` : 'Unassigned',
        sheetDims
      ]);
    });

    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'cut-list.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Grain Warning Toast */}
      {grainWarning && !showBottomSheet && (
        <div className="fixed top-4 left-4 right-4 z-50 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl shadow-lg flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm font-medium">{grainWarning}</span>
        </div>
      )}
      {/* Header */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        onChange={handleCSVUpload}
        style={{ display: 'none' }}
      />

      {/* Add Buttons */}
      <div className="p-4 bg-gray-50 border-b border-gray-200">
        {/* Selected sheet indicator */}
        {selectedStockTypeId && stockSheetTypes.find(t => t.id === selectedStockTypeId) && (
          <div className="mb-2 text-xs text-accent-600 font-medium text-center">
            Adding to: {stockSheetTypes.find(t => t.id === selectedStockTypeId)?.name}
          </div>
        )}
        <div className="flex gap-2">
          <button
            onClick={openAddSheet}
            className="flex-[1.5] flex items-center justify-center gap-1.5 px-3 py-4 bg-accent-500 text-white rounded-xl font-bold text-base shadow-md hover:bg-accent-600 active:bg-accent-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add Part
          </button>
          <button
            onClick={openAddStockType}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-4 border-2 border-sage-400 text-sage-700 rounded-xl font-semibold hover:bg-sage-50 active:bg-sage-100 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Sheet
          </button>
          <button
            onClick={handleClickCSVUpload}
            className="flex items-center justify-center px-3 py-4 border-2 border-sage-400 text-sage-700 rounded-xl hover:bg-sage-50 active:bg-sage-100 transition-colors"
            title="Upload CSV"
          >
            <Upload className="w-5 h-5" />
          </button>
          <button
            onClick={exportCSV}
            disabled={pieces.length === 0}
            className="flex items-center justify-center px-3 py-4 border-2 border-sage-400 text-sage-700 rounded-xl hover:bg-sage-50 active:bg-sage-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            title="Download CSV"
          >
            <Download className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Parts List - Grouped by Stock Type */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {pieces.length === 0 && stockSheetTypes.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 mb-2">No parts added yet</p>
            <p className="text-sm text-gray-400">Start by adding a sheet type, then add parts</p>
          </div>
        ) : (
          stockTypeGroups.map((group) => (
            <div
              key={`stocktype-${group.stockType.id}`}
              className={`bg-white rounded-xl overflow-hidden shadow-sm border-2 ${
                selectedStockTypeId === group.stockType.id
                  ? 'border-accent-400'
                  : group.stockType.id === 'unassigned'
                  ? 'border-gray-200'
                  : 'border-sage-300'
              }`}
            >
              {/* Group Header */}
              <div
                className={`px-4 py-3 flex items-center justify-between transition-colors ${
                  selectedStockTypeId === group.stockType.id
                    ? 'bg-accent-100'
                    : group.stockType.id === 'unassigned'
                    ? 'bg-gray-100'
                    : 'bg-sage-100'
                }`}
              >
                <div className="flex items-center gap-3">
                  {/* Selection radio circle */}
                  {group.stockType.id !== 'unassigned' ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedStockTypeId(
                          selectedStockTypeId === group.stockType.id ? null : group.stockType.id
                        );
                      }}
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                        selectedStockTypeId === group.stockType.id
                          ? 'border-accent-500 bg-accent-500'
                          : 'border-gray-400 bg-white'
                      }`}
                    >
                      {selectedStockTypeId === group.stockType.id && (
                        <div className="w-2.5 h-2.5 rounded-full bg-white" />
                      )}
                    </button>
                  ) : (
                    <div className="w-6" />
                  )}
                  <div className="flex-1 min-w-0">
                    <span className="font-semibold text-gray-700 block">
                      {group.stockType.name}
                    </span>
                    <span className="text-xs text-gray-500 flex flex-wrap items-center gap-1">
                      <span>{formatSheetSize(group.stockType.width, group.stockType.length)} • {formatThickness(group.stockType.thickness)} • {group.pieces.length} part{group.pieces.length !== 1 ? 's' : ''}</span>
                      {getSheetsNeededForStockType(group.stockType.id) > 0 && (
                        <span className="inline-flex items-center whitespace-nowrap px-2 py-px bg-accent-100 text-accent-600 rounded-full font-medium">
                          → {getSheetsNeededForStockType(group.stockType.id)} sheet{getSheetsNeededForStockType(group.stockType.id) !== 1 ? 's' : ''}
                        </span>
                      )}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {group.stockType.id !== 'unassigned' && (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditStockType(group.stockType);
                        }}
                        className="p-1.5 text-gray-400 hover:text-accent-500"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirm({ id: group.stockType.id, name: group.stockType.name });
                        }}
                        className="p-1.5 text-gray-400 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleGroupCollapse(group.stockType.id);
                    }}
                    className="p-1.5 text-gray-400"
                  >
                    {collapsedGroups[group.stockType.id] ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronUp className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* Group Pieces */}
              {!collapsedGroups[group.stockType.id] && (
                <div className="divide-y divide-gray-100">
                  {group.pieces.map((piece) => (
                    <div
                      key={piece.id}
                      onClick={() => openEditSheet(piece)}
                      className="px-4 py-3 active:bg-gray-50"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 truncate">{piece.name}</h3>
                          <p className="text-sm text-gray-500" title={`${formatPreciseDimension(piece.width, currentUnits.unit, currentUnits.system)} × ${formatPreciseDimension(piece.length, currentUnits.unit, currentUnits.system)}`}>
                            {formatDimension(piece.width, currentUnits.unit, currentUnits.system, { includeUnit: false })} × {formatDimension(piece.length, currentUnits.unit, currentUnits.system, { includeUnit: false })} {getUnitLabel()}
                            {piece.thickness > 0 && (
                              <span className="text-gray-400"> • {formatThickness(piece.thickness)}</span>
                            )}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const cycleOrder = ['none', 'horizontal', 'vertical'];
                              const currentIdx = cycleOrder.indexOf(piece.grainDirection || 'none');
                              const sheetW = group.stockType.width;
                              const sheetL = group.stockType.length;
                              // Find next valid grain direction
                              for (let i = 1; i <= 3; i++) {
                                const nextGrain = cycleOrder[(currentIdx + i) % 3];
                                if (isGrainDirectionValid(piece.width, piece.length, nextGrain, sheetW, sheetL)) {
                                  updatePiece(piece.id, 'grainDirection', nextGrain);
                                  setGrainWarning(null);
                                  return;
                                }
                              }
                              // No valid grain - show warning
                              setGrainWarning('Grain direction limited by sheet dimensions');
                              setTimeout(() => setGrainWarning(null), 3000);
                            }}
                            className="p-1"
                            title={!isGrainDirectionValid(piece.width, piece.length, piece.grainDirection || 'none', group.stockType.width, group.stockType.length) ? 'Current grain invalid for sheet size' : ''}
                          >
                            {piece.grainDirection === 'horizontal' ? <ArrowLeftRight className={`w-5 h-5 ${!isGrainDirectionValid(piece.width, piece.length, 'horizontal', group.stockType.width, group.stockType.length) ? 'text-red-400' : 'text-gray-400'}`} /> : piece.grainDirection === 'vertical' ? <ArrowUpDown className={`w-5 h-5 ${!isGrainDirectionValid(piece.width, piece.length, 'vertical', group.stockType.width, group.stockType.length) ? 'text-red-400' : 'text-gray-400'}`} /> : <Circle className="w-5 h-5 text-gray-400" />}
                          </button>
                          <span className="text-lg font-bold text-accent-400">x{piece.quantity}</span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeletePieceConfirm({ id: piece.id, name: piece.name });
                            }}
                            className="p-2 text-gray-400 hover:text-red-600"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                      {false && piece.grainDirection !== 'none' && (
                        <div className="mt-1 text-xs text-gray-400">
                          Grain: {piece.grainDirection}
                        </div>
                      )}
                    </div>
                  ))}
                  {group.pieces.length === 0 && group.stockType.id !== 'unassigned' && (
                    <div className="px-4 py-6 text-center text-gray-400 text-sm">
                      {selectedStockTypeId === group.stockType.id
                        ? '✓ Selected — tap "Add Part" above'
                        : 'Tap header to select, then "Add Part"'}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Bottom Sheet for Add/Edit Piece */}
      {showBottomSheet && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setShowBottomSheet(false)}
          />

          {/* Sheet */}
          <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-h-[85vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-bold">
                {editingPiece ? 'Edit Part' : 'Add Part'}
              </h2>
              <button
                onClick={() => setShowBottomSheet(false)}
                className="p-2 text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Name */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 text-lg border border-gray-300 rounded-xl focus:border-accent-400 focus:outline-none"
                  placeholder="Part name"
                />
              </div>

              {/* Dimensions */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">
                    Width ({getUnitLabel()})
                  </label>
                  <input
                    type="text"
                    inputMode="text"
                    value={formData.width}
                    title={editingPiece ? formatPreciseDimension(editingPiece.width, currentUnits.unit, currentUnits.system) : ''}
                    onChange={(e) => {
                      const newWidth = e.target.value;
                      const pieceW = parseMathExpr(newWidth) || 0;
                      const pieceL = parseMathExpr(formData.length) || 0;
                      const { width: sheetW, length: sheetL } = getSheetDimensionsForStockType(formData.stockSheetTypeId || selectedStockTypeId);
                      const autoGrain = getAutoGrainDirection(pieceW, pieceL, sheetW, sheetL);
                      setFormData({ ...formData, width: newWidth, grainDirection: autoGrain });
                    }}
                    onBlur={() => evalBlur(formData.width, v => setFormData(f => ({ ...f, width: v })))}
                    className="w-full px-3 py-3 text-lg border border-gray-300 rounded-xl focus:border-accent-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">
                    Length ({getUnitLabel()})
                  </label>
                  <input
                    type="text"
                    inputMode="text"
                    value={formData.length}
                    title={editingPiece ? formatPreciseDimension(editingPiece.length, currentUnits.unit, currentUnits.system) : ''}
                    onChange={(e) => {
                      const newLength = e.target.value;
                      const pieceW = parseMathExpr(formData.width) || 0;
                      const pieceL = parseMathExpr(newLength) || 0;
                      const { width: sheetW, length: sheetL } = getSheetDimensionsForStockType(formData.stockSheetTypeId || selectedStockTypeId);
                      const autoGrain = getAutoGrainDirection(pieceW, pieceL, sheetW, sheetL);
                      setFormData({ ...formData, length: newLength, grainDirection: autoGrain });
                    }}
                    onBlur={() => evalBlur(formData.length, v => setFormData(f => ({ ...f, length: v })))}
                    className="w-full px-3 py-3 text-lg border border-gray-300 rounded-xl focus:border-accent-400 focus:outline-none"
                  />
                </div>
              </div>

              {/* Sheet Assignment Info */}
              <div className="bg-gray-50 p-3 rounded-xl">
                {stockSheetTypes.length > 0 ? (
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-1">Assigned Sheet</label>
                    <select
                      value={formData.stockSheetTypeId || ''}
                      onChange={(e) => setFormData({ ...formData, stockSheetTypeId: e.target.value || null })}
                      className="w-full px-4 py-3 text-base border border-gray-300 rounded-xl focus:border-accent-400 focus:outline-none bg-white"
                    >
                      <option value="">Unassigned (Default)</option>
                      {stockSheetTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name} ({formatSheetSize(type.width, type.length)})
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-2">
                      Thickness: <span className="font-medium">{formatThickness(getThicknessForSelectedSheet(formData.stockSheetTypeId || selectedStockTypeId))}</span>
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">
                    Sheet: <span className="font-medium">Unassigned (Default)</span> • Thickness: {formatThickness(getThicknessForSelectedSheet(null))}
                  </p>
                )}
              </div>

              {/* Quantity */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Quantity</label>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setFormData({ ...formData, quantity: Math.max(1, parseInt(formData.quantity) - 1).toString() })}
                    className="w-12 h-12 rounded-full bg-gray-100 text-xl font-bold text-gray-700"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    inputMode="numeric"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    onFocus={(e) => e.target.select()}
                    className="flex-1 px-4 py-3 text-lg text-center border border-gray-300 rounded-xl focus:border-accent-400 focus:outline-none"
                  />
                  <button
                    onClick={() => setFormData({ ...formData, quantity: (parseInt(formData.quantity) + 1).toString() })}
                    className="w-12 h-12 rounded-full bg-gray-100 text-xl font-bold text-gray-700"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Grain Direction */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">Grain Direction</label>
                {(() => {
                  // Get piece dimensions
                  const pieceW = parseMathExpr(formData.width) || 0;
                  const pieceL = parseMathExpr(formData.length) || 0;
                  // Get sheet dimensions
                  const targetStockTypeId = formData.stockSheetTypeId || selectedStockTypeId;
                  const { width: sheetW, length: sheetL } = getSheetDimensionsForStockType(targetStockTypeId);
                  // Check which grains are valid
                  const validGrains = {
                    none: isGrainDirectionValid(pieceW, pieceL, 'none', sheetW, sheetL),
                    horizontal: isGrainDirectionValid(pieceW, pieceL, 'horizontal', sheetW, sheetL),
                    vertical: isGrainDirectionValid(pieceW, pieceL, 'vertical', sheetW, sheetL),
                  };
                  const cycleOrder = ['none', 'horizontal', 'vertical'];
                  const validCount = Object.values(validGrains).filter(Boolean).length;
                  const handleGrainClick = () => {
                    // If only one grain direction is valid, grain is locked
                    if (validCount === 1) {
                      setGrainWarning('Grain direction for this part is limited by sheet dimensions');
                      setTimeout(() => setGrainWarning(null), 3000);
                      return;
                    }
                    const currentIdx = cycleOrder.indexOf(formData.grainDirection);
                    // Try to find next valid grain direction
                    for (let i = 1; i <= 3; i++) {
                      const nextGrain = cycleOrder[(currentIdx + i) % 3];
                      if (validGrains[nextGrain]) {
                        setFormData({ ...formData, grainDirection: nextGrain });
                        setGrainWarning(null);
                        return;
                      }
                    }
                    // No valid grain directions - show warning
                    setGrainWarning('Part is too large to fit on sheet with any grain direction');
                    setTimeout(() => setGrainWarning(null), 3000);
                  };

                  return (
                    <>
                      <div className="flex justify-center">
                        <button
                          onClick={handleGrainClick}
                          title={formData.grainDirection === 'horizontal' ? 'Along Length' : formData.grainDirection === 'vertical' ? 'Along Width' : 'No Preference'}
                          className={`flex items-center gap-3 px-6 py-3 rounded-xl transition-colors ${
                            !validGrains[formData.grainDirection] ? 'text-red-600 bg-red-50' : 'text-gray-600 hover:brightness-95'
                          }`}
                          style={validGrains[formData.grainDirection] ? { backgroundColor: '#E5E7EB' } : {}}
                        >
                          {formData.grainDirection === 'horizontal' ? <ArrowLeftRight className="w-6 h-6" /> : formData.grainDirection === 'vertical' ? <ArrowUpDown className="w-6 h-6" /> : <Circle className="w-6 h-6" />}
                          <span className="text-sm font-medium">
                            {formData.grainDirection === 'horizontal' ? 'Along Length' : formData.grainDirection === 'vertical' ? 'Along Width' : 'No Preference'}
                          </span>
                        </button>
                      </div>
                      {!validGrains[formData.grainDirection] && (
                        <p className="text-xs text-red-500 text-center mt-2">
                          <AlertTriangle className="w-3 h-3 inline mr-1" />
                          Part won't fit on sheet with this grain direction
                        </p>
                      )}
                      {grainWarning && (
                        <p className="text-xs text-red-500 text-center mt-2">
                          <AlertTriangle className="w-3 h-3 inline mr-1" />
                          {grainWarning}
                        </p>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* Save Button */}
              <button
                onClick={handleSave}
                className="w-full py-4 bg-accent-400 text-white rounded-xl font-bold text-lg hover:bg-accent-500 transition-colors"
              >
                {editingPiece ? 'Save Changes' : 'Add Part'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Stock Type Bottom Sheet */}
      {showStockTypeSheet && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setShowStockTypeSheet(false)}
          />

          {/* Sheet */}
          <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-h-[85vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-bold">
                {editingStockType ? 'Edit Sheet Type' : 'Add Sheet Type'}
              </h2>
              <button
                onClick={() => setShowStockTypeSheet(false)}
                className="p-2 text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Material Name */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Material Name</label>
                <input
                  type="text"
                  value={stockTypeForm.name}
                  onChange={(e) => setStockTypeForm({ ...stockTypeForm, name: e.target.value })}
                  placeholder={`Stock ${stockSheetTypes.length + 1}`}
                  className="w-full px-4 py-3 text-lg border border-gray-300 rounded-xl focus:border-accent-400 focus:outline-none"
                />
              </div>

              {/* Thickness */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">
                  Thickness ({getUnitLabel()})
                </label>
                <input
                  type="text"
                  inputMode="text"
                  placeholder="e.g. 3/4"
                  value={stockTypeForm.thickness}
                  onChange={(e) => setStockTypeForm({ ...stockTypeForm, thickness: e.target.value })}
                  onBlur={() => evalBlur(stockTypeForm.thickness, v => setStockTypeForm(f => ({ ...f, thickness: v })))}
                  className="w-full px-4 py-3 text-lg border border-gray-300 rounded-xl focus:border-accent-400 focus:outline-none"
                />
              </div>

              {/* Dimensions */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">
                    Width ({getUnitLabel()})
                  </label>
                  <input
                    type="text"
                    inputMode="text"
                    value={stockTypeForm.width}
                    onChange={(e) => setStockTypeForm({ ...stockTypeForm, width: e.target.value })}
                    onBlur={() => evalBlur(stockTypeForm.width, v => setStockTypeForm(f => ({ ...f, width: v })))}
                    className="w-full px-4 py-3 text-lg border border-gray-300 rounded-xl focus:border-accent-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">
                    Length ({getUnitLabel()})
                  </label>
                  <input
                    type="text"
                    inputMode="text"
                    value={stockTypeForm.length}
                    onChange={(e) => setStockTypeForm({ ...stockTypeForm, length: e.target.value })}
                    onBlur={() => evalBlur(stockTypeForm.length, v => setStockTypeForm(f => ({ ...f, length: v })))}
                    className="w-full px-4 py-3 text-lg border border-gray-300 rounded-xl focus:border-accent-400 focus:outline-none"
                  />
                </div>
              </div>

              {/* Width > Length Warning */}
              {parseMathExpr(stockTypeForm.width) > parseMathExpr(stockTypeForm.length) && parseMathExpr(stockTypeForm.length) > 0 && (
                <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                  <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-amber-800">
                      Width is larger than length. For best results, swap these values.
                    </p>
                    <button
                      type="button"
                      onClick={() => setStockTypeForm({
                        ...stockTypeForm,
                        width: stockTypeForm.length,
                        length: stockTypeForm.width
                      })}
                      className="mt-2 text-sm font-medium text-amber-700 hover:text-amber-900 underline"
                    >
                      Swap width and length
                    </button>
                  </div>
                </div>
              )}

              {/* Quick Presets */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">Common Sizes</label>
                <div className="grid grid-cols-3 gap-2">
                  {currentUnits.system === 'imperial' ? (
                    <>
                      <button
                        onClick={() => setStockTypeForm({ ...stockTypeForm, width: '48', length: '96' })}
                        className="px-3 py-3 bg-sage-100 text-gray-700 rounded-xl font-medium"
                      >
                        4×8 ft
                      </button>
                      <button
                        onClick={() => setStockTypeForm({ ...stockTypeForm, width: '60', length: '60' })}
                        className="px-3 py-3 bg-sage-100 text-gray-700 rounded-xl font-medium"
                      >
                        5×5 ft
                      </button>
                      <button
                        onClick={() => setStockTypeForm({ ...stockTypeForm, width: '48', length: '48' })}
                        className="px-3 py-3 bg-sage-100 text-gray-700 rounded-xl font-medium"
                      >
                        4×4 ft
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => setStockTypeForm({ ...stockTypeForm, width: '122', length: '244' })}
                        className="px-3 py-3 bg-sage-100 text-gray-700 rounded-xl font-medium"
                      >
                        122×244
                      </button>
                      <button
                        onClick={() => setStockTypeForm({ ...stockTypeForm, width: '125', length: '250' })}
                        className="px-3 py-3 bg-sage-100 text-gray-700 rounded-xl font-medium"
                      >
                        125×250
                      </button>
                      <button
                        onClick={() => setStockTypeForm({ ...stockTypeForm, width: '122', length: '122' })}
                        className="px-3 py-3 bg-sage-100 text-gray-700 rounded-xl font-medium"
                      >
                        122×122
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Save Button */}
              <button
                onClick={saveStockType}
                className="w-full py-4 bg-sage-500 text-white rounded-xl font-bold text-lg hover:bg-sage-600 transition-colors"
              >
                {editingStockType ? 'Save Changes' : 'Add Sheet Type'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* CSV Column Mapper Dialog */}
      <CSVColumnMapper
        isOpen={showColumnMapper}
        onClose={() => setShowColumnMapper(false)}
        onConfirm={processCSVWithMapping}
        headers={csvData.headers}
        sampleData={csvData.rows}
      />

      {/* Delete Sheet Confirmation */}
      <ConfirmDialog
        isOpen={!!deleteConfirm}
        title={`Delete "${deleteConfirm?.name}"?`}
        message="All parts assigned to this sheet will become unassigned and move to the default 96×48 in sheet."
        confirmLabel="Delete Sheet"
        onConfirm={() => {
          removeStockSheetType(deleteConfirm.id);
          setDeleteConfirm(null);
        }}
        onCancel={() => setDeleteConfirm(null)}
      />

      {/* Delete Piece Confirmation */}
      <ConfirmDialog
        isOpen={!!deletePieceConfirm}
        title={`Delete "${deletePieceConfirm?.name}"?`}
        message="This part will be permanently removed from your cut list."
        confirmLabel="Delete Part"
        onConfirm={() => {
          removePiece(deletePieceConfirm.id);
          setDeletePieceConfirm(null);
        }}
        onCancel={() => setDeletePieceConfirm(null)}
      />
    </div>
  );
};

export default PartsStep;
